/**
 * Settings → Notifications: text-message alerts. The opt-in is the screen the
 * SMS sender registration describes (docs/SMS.md): the member types their own
 * number, ticks an unticked box beside the exact consent text, and confirms
 * the number with a texted code before any alert is sent.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { SmsOptIn, SmsSubscription } from "@sable/api-contract";
import { brand } from "@sable/brand";
import { SMS_CONSENT_VERSION, smsConsentText, usPhoneE164 } from "@sable/domain";
import { t } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { shortDate } from "../lib/format.ts";
import { keys } from "../lib/queries.ts";
import { useWs } from "../lib/workspace.tsx";
import { ErrorBlock, Field, Loading } from "./ui.tsx";

export function SmsCard() {
  const ws = useWs();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: keys.sms(ws.id), queryFn: () => request<SmsSubscription>("/notifications/sms", { workspace: ws.id }) });
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState("");
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function run(path: string, method: "POST" | "DELETE", body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      const next = await request<SmsSubscription>(path, { method, workspace: ws.id, body });
      qc.setQueryData(keys.sms(ws.id), next);
      setCode("");
      setChanging(false);
      if (next.status !== "pending") { setPhone(""); setConsent(false); }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const sub = q.data;
  const showForm = !sub || sub.status === "none" || sub.status === "opted_out" || changing;
  const validPhone = usPhoneE164(phone) !== null;
  const termsUrl = `https://${brand.siteDomain}/terms`;
  const privacyUrl = `https://${brand.siteDomain}/privacy`;

  return (
    <section className="card stack" id="notifications">
      <div>
        <h2>{t("sms.title")}</h2>
        <h3 className="small">{t("sms.subtitle")}</h3>
        <p className="muted small">{t("sms.hint")}</p>
      </div>

      {q.isPending ? <Loading /> : q.isError ? <ErrorBlock error={q.error} onRetry={() => void q.refetch()} /> : showForm ? (
        <form className="stack" onSubmit={(e) => {
          e.preventDefault();
          void run("/notifications/sms", "POST", { phone, consent: true, consentVersion: SMS_CONSENT_VERSION } satisfies SmsOptIn);
        }}>
          {sub?.status === "opted_out" && !changing && <p className="small">{t("sms.off")}</p>}
          <Field label={t("sms.phone")} hint={phone && !validPhone ? t("sms.usOnly") : undefined}>
            <input className="input" type="tel" inputMode="tel" autoComplete="tel" value={phone} maxLength={30}
              placeholder={t("sms.phonePlaceholder")} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <label className="consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>
              {smsConsentText(brand.displayName)}{" "}
              <a href={termsUrl} target="_blank" rel="noreferrer">{t("sms.terms")}</a>
              {" · "}
              <a href={privacyUrl} target="_blank" rel="noreferrer">{t("sms.privacy")}</a>
            </span>
          </label>
          <div className="row-wrap">
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy || !consent || !validPhone}>
              {busy ? t("sms.sending") : t("sms.sendCode")}
            </button>
            {changing && <button className="btn btn-ghost btn-sm" type="button" onClick={() => setChanging(false)}>{t("app.cancel")}</button>}
          </div>
        </form>
      ) : sub.status === "pending" ? (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void run("/notifications/sms/verify", "POST", { code }); }}>
          <p className="small">{t("sms.codeSent", { phone: sub.phone ?? "" })}</p>
          <Field label={t("sms.code")}>
            <input className="input" inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </Field>
          <div className="row-wrap">
            <button className="btn btn-primary btn-sm" type="submit" disabled={busy || code.length !== 6}>{t("sms.verify")}</button>
            <button className="btn btn-ghost btn-sm" type="button" disabled={busy}
              onClick={() => void run("/notifications/sms/resend", "POST")}>{t("sms.resend")}</button>
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setChanging(true)}>{t("sms.change")}</button>
          </div>
        </form>
      ) : (
        <div className="stack">
          <div className="banner banner-ok">{t("sms.on", { phone: sub.phone ?? "", when: shortDate(sub.verifiedAt) })}</div>
          <div className="row-wrap">
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setChanging(true)}>{t("sms.change")}</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void run("/notifications/sms", "DELETE")}>{t("sms.turnOff")}</button>
          </div>
        </div>
      )}
      {error ? <ErrorBlock error={error} /> : null}
    </section>
  );
}
